import * as React from 'react';

import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { padDigits } from '@/components/ui/shadcn/custom/DateTimePicker/segmented-input-utils';
import { getPeriod } from './time-utils';
import { cn } from 'cn';

import {
  useTimePicker,
  type TimePickerController,
  type TimePopoverLayout,
  type TimePrecision,
  type UseTimePickerOptions,
} from './use-time-picker';

function pad(value: number) {
  return padDigits(value, 2);
}

function formatPaddedOption(option: number | string) {
  return pad(Number(option));
}

function useScrollOptionIntoView(
  containerRef: React.RefObject<HTMLElement | null>,
  optionRefs: React.RefObject<Record<string, HTMLButtonElement | null>>,
  scrollToKey: string | undefined,
  scrollTrigger: number | undefined,
  // The raw (pre-fallback) selection value. A fallback-centered row can share the
  // same key as the option that later gets selected there, which would otherwise
  // leave this effect's dependencies unchanged and skip re-centering after the user
  // scrolls away and then picks that exact option.
  selectionSignal?: unknown
) {
  React.useEffect(() => {
    if (scrollToKey === undefined) return;

    const scrollSelectedIntoView = () => {
      const viewport = containerRef.current?.querySelector<HTMLElement>(
        '[data-slot="scroll-area-viewport"]'
      );
      const selectedButton = optionRefs.current?.[scrollToKey];
      if (!viewport || !selectedButton) return;
      viewport.scrollTo({
        top:
          selectedButton.offsetTop -
          viewport.clientHeight / 2 +
          selectedButton.offsetHeight / 2,
        behavior: 'smooth',
      });
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(scrollSelectedIntoView);
    });
  }, [containerRef, optionRefs, scrollToKey, scrollTrigger, selectionSignal]);
}

// Roving tabindex for a list/grid of option buttons (WAI-ARIA APG listbox pattern):
// exactly one option is in the Tab order at a time (the selected option, or the first
// option if nothing is selected yet); arrow keys move focus between options without
// changing selection, matching how a sighted user would visually scan the list.
function useRovingTabIndex({
  optionRefs,
  orderedKeys,
  columns,
  activeKey,
}: {
  optionRefs: React.RefObject<Record<string, HTMLButtonElement | null>>;
  orderedKeys: string[];
  columns: number;
  activeKey?: string;
}) {
  const tabbableKey = activeKey ?? orderedKeys[0];

  const getTabIndex = (key: string) => (key === tabbableKey ? 0 : -1);

  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    const currentKey = (event.target as HTMLElement).dataset.optionKey;
    if (currentKey === undefined) return;
    const index = orderedKeys.indexOf(currentKey);
    if (index === -1) return;

    let nextIndex: number | undefined;
    switch (event.key) {
      case 'ArrowDown':
        nextIndex = index + columns;
        break;
      case 'ArrowUp':
        nextIndex = index - columns;
        break;
      case 'ArrowRight':
        if (columns > 1) nextIndex = index + 1;
        break;
      case 'ArrowLeft':
        if (columns > 1) nextIndex = index - 1;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = orderedKeys.length - 1;
        break;
      case 'PageUp':
        nextIndex = Math.max(0, index - columns * 5);
        break;
      case 'PageDown':
        nextIndex = Math.min(orderedKeys.length - 1, index + columns * 5);
        break;
      default:
        return;
    }

    if (
      nextIndex === undefined ||
      nextIndex < 0 ||
      nextIndex >= orderedKeys.length
    ) {
      return;
    }

    event.preventDefault();
    optionRefs.current?.[orderedKeys[nextIndex]]?.focus();
  };

  return { getTabIndex, onKeyDown };
}

type TimeColumnProps = {
  label: string;
  options: number[] | string[];
  selected?: number | string;
  formatOption?: (option: number | string) => string;
  onSelect: (option: number | string) => void;
  onDeselect?: () => void;
  scrollTrigger?: number;
  fullHeight?: boolean;
  viewportHeightClassName?: string;
  viewportHeightPx?: number;
  fallbackScrollIndex?: number;
};

function TimeColumn({
  label,
  options,
  selected,
  formatOption = String,
  onSelect,
  onDeselect,
  scrollTrigger,
  fullHeight = false,
  viewportHeightClassName,
  viewportHeightPx,
  fallbackScrollIndex,
}: TimeColumnProps) {
  const columnRef = React.useRef<HTMLDivElement>(null);
  const optionRefs = React.useRef<Record<string, HTMLButtonElement | null>>({});
  // Enough top/bottom padding that any option (including the first/last) can be
  // scrolled to the vertical center of the viewport.
  const buttonBlockHeight = 36;
  const dynamicPaddingPx = viewportHeightPx
    ? Math.max(16, (viewportHeightPx - buttonBlockHeight) / 2)
    : undefined;
  const contentPaddingClassName = dynamicPaddingPx
    ? undefined
    : fullHeight
      ? 'tw:py-2'
      : viewportHeightClassName === 'tw:h-80'
        ? 'tw:py-36'
        : viewportHeightClassName === 'tw:h-72'
          ? 'tw:py-32'
          : 'tw:py-16';
  const selectedIndex =
    selected === undefined
      ? -1
      : options.findIndex((option) => option === selected);
  const selectedKey = selectedIndex < 0 ? undefined : String(selected);
  // When nothing is selected, scroll to a fixed reference option instead of leaving
  // the column at the very top, so the viewport starts filled instead of blank.
  const fallbackScrollKey =
    selectedKey === undefined &&
    fallbackScrollIndex !== undefined &&
    options.length > 0
      ? String(options[Math.min(fallbackScrollIndex, options.length - 1)])
      : undefined;

  useScrollOptionIntoView(
    columnRef,
    optionRefs,
    selectedKey ?? fallbackScrollKey,
    scrollTrigger,
    selected
  );

  const orderedKeys = React.useMemo(
    () => options.map((option) => String(option)),
    [options]
  );
  const { getTabIndex, onKeyDown } = useRovingTabIndex({
    optionRefs,
    orderedKeys,
    columns: 1,
    activeKey: selectedKey ?? fallbackScrollKey,
  });

  return (
    <div
      ref={columnRef}
      className={cn(
        'tw:flex tw:min-w-14 tw:flex-col',
        fullHeight && 'tw:h-full'
      )}
      role="group"
      aria-label={`${label} selection`}
    >
      <ScrollArea
        style={viewportHeightPx ? { height: viewportHeightPx } : undefined}
        className={cn(
          'tw:w-14 tw:[&>[data-slot=scroll-area-scrollbar]]:hidden',
          !viewportHeightPx &&
            (fullHeight ? 'tw:h-full' : (viewportHeightClassName ?? 'tw:h-40'))
        )}
      >
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- keydown only implements roving tabindex between the child <button> options, which remain the actual interactive elements. */}
        <div
          style={
            dynamicPaddingPx
              ? {
                  paddingTop: dynamicPaddingPx,
                  paddingBottom: dynamicPaddingPx,
                }
              : undefined
          }
          className={cn(
            'tw:flex tw:flex-col tw:gap-1 tw:px-1',
            contentPaddingClassName
          )}
          onKeyDown={onKeyDown}
        >
          {options.map((option) => {
            const isSelected = option === selected;
            const optionKey = String(option);
            return (
              <button
                key={optionKey}
                type="button"
                aria-label={`${label} ${formatOption(option)}`}
                aria-pressed={isSelected}
                data-option-key={optionKey}
                tabIndex={getTabIndex(optionKey)}
                ref={(element) => {
                  optionRefs.current[optionKey] = element;
                }}
                className={cn(
                  'tw:flex tw:h-8 tw:w-full tw:items-center tw:justify-center tw:rounded-md tw:px-2 tw:text-sm tw:tabular-nums tw:transition-colors tw:focus-visible:outline-none tw:focus-visible:ring-[3px] tw:focus-visible:ring-ring/50',
                  isSelected
                    ? 'tw:bg-primary tw:text-primary-foreground'
                    : 'tw:text-foreground tw:hover:bg-accent tw:hover:text-accent-foreground'
                )}
                onClick={() =>
                  isSelected && onDeselect ? onDeselect() : onSelect(option)
                }
              >
                {formatOption(option)}
              </button>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}

type TimePickerRenderProps = {
  className?: string;
  fullHeight?: boolean;
  columnViewportHeightClassName?: string;
  viewportHeightPx?: number;
};

function TimePickerFromController({
  controller,
  className,
  fullHeight = false,
  columnViewportHeightClassName,
  viewportHeightPx,
}: TimePickerRenderProps & { controller: TimePickerController }) {
  const contentViewportHeightPx = viewportHeightPx
    ? Math.max(40, viewportHeightPx - 16)
    : undefined;
  const singleColumnRef = React.useRef<HTMLDivElement>(null);
  const singleColumnOptionRefs = React.useRef<
    Record<string, HTMLButtonElement | null>
  >({});
  const gridColumnRef = React.useRef<HTMLDivElement>(null);
  const gridOptionRefs = React.useRef<Record<string, HTMLButtonElement | null>>(
    {}
  );

  useScrollOptionIntoView(
    singleColumnRef,
    singleColumnOptionRefs,
    controller.dayOptionKey,
    controller.scrollTrigger
  );
  useScrollOptionIntoView(
    gridColumnRef,
    gridOptionRefs,
    controller.dayOptionKey,
    controller.scrollTrigger
  );

  const singleColumnOrderedKeys = React.useMemo(
    () => controller.dayOptions.map((option) => option.key),
    [controller.dayOptions]
  );
  const singleColumnRoving = useRovingTabIndex({
    optionRefs: singleColumnOptionRefs,
    orderedKeys: singleColumnOrderedKeys,
    columns: 1,
    activeKey: controller.dayOptionKey,
  });

  const gridOrderedKeys = React.useMemo(
    () => controller.gridRows.flat().map((option) => option.key),
    [controller.gridRows]
  );
  const gridRoving = useRovingTabIndex({
    optionRefs: gridOptionRefs,
    orderedKeys: gridOrderedKeys,
    columns: 4,
    activeKey: controller.dayOptionKey,
  });

  if (controller.showSingleColumnList) {
    return (
      <div
        ref={singleColumnRef}
        className={cn('tw:p-2', fullHeight && 'tw:h-full', className)}
      >
        <ScrollArea
          style={
            contentViewportHeightPx
              ? { height: contentViewportHeightPx }
              : undefined
          }
          className={cn(
            'tw:w-24 tw:[&>[data-slot=scroll-area-scrollbar]]:hidden',
            !contentViewportHeightPx &&
              (fullHeight
                ? 'tw:h-full'
                : (columnViewportHeightClassName ?? 'tw:h-40'))
          )}
        >
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- keydown only implements roving tabindex between the child <button> options, which remain the actual interactive elements. */}
          <div
            className="tw:flex tw:flex-col tw:gap-1 tw:px-1 tw:py-1"
            onKeyDown={singleColumnRoving.onKeyDown}
          >
            {controller.dayOptions.map((option) => {
              const isSelected = option.key === controller.dayOptionKey;
              return (
                <button
                  key={option.key}
                  type="button"
                  data-option-key={option.key}
                  tabIndex={singleColumnRoving.getTabIndex(option.key)}
                  ref={(element) => {
                    singleColumnOptionRefs.current[option.key] = element;
                  }}
                  className={cn(
                    'tw:flex tw:h-8 tw:w-full tw:items-center tw:justify-center tw:rounded-md tw:px-2 tw:text-sm tw:tabular-nums tw:transition-colors tw:focus-visible:outline-none tw:focus-visible:ring-[3px] tw:focus-visible:ring-ring/50',
                    isSelected
                      ? 'tw:bg-primary tw:text-primary-foreground'
                      : 'tw:text-foreground tw:hover:bg-accent tw:hover:text-accent-foreground'
                  )}
                  onClick={() =>
                    isSelected
                      ? controller.clear()
                      : controller.selectOption(option.parts)
                  }
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </ScrollArea>
      </div>
    );
  }

  if (controller.showFourColumnGrid) {
    return (
      <div
        ref={gridColumnRef}
        className={cn('tw:pt-8', fullHeight && 'tw:h-full', className)}
      >
        {controller.fourColumnHeaderGroups && (
          <div className="tw:px-2 tw:pb-1">
            <div className="tw:grid tw:grid-cols-4 tw:gap-x-1 tw:text-[10px] tw:leading-4 tw:uppercase tw:tracking-wide tw:text-muted-foreground">
              <div className="tw:col-span-2 tw:text-center tw:font-medium">
                {controller.fourColumnHeaderGroups[0]}
              </div>
              <div className="tw:col-span-2 tw:text-center tw:font-medium">
                {controller.fourColumnHeaderGroups[1]}
              </div>
              <div className="tw:text-center tw:font-normal">:00</div>
              <div className="tw:text-center tw:font-normal">:30</div>
              <div className="tw:text-center tw:font-normal">:00</div>
              <div className="tw:text-center tw:font-normal">:30</div>
            </div>
            <Separator className="tw:mt-1 tw:bg-border/45" />
          </div>
        )}
        <ScrollArea
          style={
            contentViewportHeightPx
              ? { height: contentViewportHeightPx }
              : undefined
          }
          className={cn(
            'tw:w-[280px] tw:[&>[data-slot=scroll-area-scrollbar]]:hidden',
            !contentViewportHeightPx &&
              (fullHeight
                ? 'tw:h-full'
                : (columnViewportHeightClassName ?? 'tw:h-40'))
          )}
        >
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- keydown only implements roving tabindex between the child <button> options, which remain the actual interactive elements. */}
          <div
            className="tw:grid tw:grid-cols-4 tw:gap-x-1 tw:gap-y-1 tw:px-1 tw:py-1"
            onKeyDown={gridRoving.onKeyDown}
          >
            {controller.gridRows.map((row, rowIndex) => (
              <React.Fragment key={`row-${row[0]?.key ?? rowIndex}`}>
                {rowIndex > 0 && (
                  <div className="tw:col-span-4 tw:px-1" aria-hidden="true">
                    <Separator className="tw:bg-border/35" />
                  </div>
                )}
                {row.map((option) => {
                  const isSelected = option.key === controller.dayOptionKey;

                  return (
                    <button
                      key={option.key}
                      type="button"
                      aria-label={option.label}
                      data-option-key={option.key}
                      tabIndex={gridRoving.getTabIndex(option.key)}
                      ref={(element) => {
                        gridOptionRefs.current[option.key] = element;
                      }}
                      className={cn(
                        'tw:flex tw:h-10 tw:min-w-0 tw:items-center tw:justify-center tw:rounded-md tw:px-2 tw:py-2.5 tw:tabular-nums tw:transition-colors tw:focus-visible:outline-none tw:focus-visible:ring-[3px] tw:focus-visible:ring-ring/50',
                        isSelected
                          ? 'tw:bg-primary tw:text-primary-foreground'
                          : 'tw:text-foreground tw:hover:bg-accent tw:hover:text-accent-foreground'
                      )}
                      onClick={() =>
                        isSelected
                          ? controller.clear()
                          : controller.selectOption(option.parts)
                      }
                    >
                      {option.period ? (
                        <span className="tw:inline-flex tw:items-baseline tw:gap-1">
                          <span className="tw:text-xs tw:font-semibold tw:tracking-[0.01em]">
                            {option.label.slice(0, -3)}
                          </span>
                          <span
                            className={cn(
                              'tw:text-[10px] tw:font-medium tw:uppercase tw:tracking-wide',
                              isSelected
                                ? 'tw:text-primary-foreground/75'
                                : 'tw:text-muted-foreground'
                            )}
                          >
                            {option.period}
                          </span>
                        </span>
                      ) : (
                        <span className="tw:text-xs tw:font-semibold tw:tracking-[0.01em]">
                          {option.label}
                        </span>
                      )}
                    </button>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </ScrollArea>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'tw:flex tw:gap-1 tw:p-2',
        fullHeight ? 'tw:h-full tw:items-stretch' : 'tw:items-center',
        className
      )}
    >
      <TimeColumn
        label="Hour"
        options={controller.hours}
        selected={controller.currentDisplayHour}
        formatOption={formatPaddedOption}
        onSelect={controller.selectHour}
        onDeselect={() => controller.clearSegment('hour')}
        scrollTrigger={controller.scrollTrigger}
        fullHeight={fullHeight}
        viewportHeightClassName={columnViewportHeightClassName}
        viewportHeightPx={contentViewportHeightPx}
        fallbackScrollIndex={4}
      />
      {controller.precision !== 'hour' && (
        <>
          <span
            style={
              contentViewportHeightPx
                ? { height: contentViewportHeightPx }
                : undefined
            }
            className={cn(
              'tw:flex tw:items-center tw:text-lg tw:font-medium',
              !contentViewportHeightPx &&
                (fullHeight
                  ? 'tw:h-full'
                  : (columnViewportHeightClassName ?? 'tw:h-40'))
            )}
          >
            :
          </span>
          <TimeColumn
            label="Minute"
            options={controller.minutes}
            selected={controller.timeParts.minute}
            formatOption={formatPaddedOption}
            onSelect={(option) =>
              controller.commit({
                ...controller.timeParts,
                minute: Number(option),
              })
            }
            onDeselect={() => controller.clearSegment('minute')}
            scrollTrigger={controller.scrollTrigger}
            fullHeight={fullHeight}
            viewportHeightClassName={columnViewportHeightClassName}
            viewportHeightPx={contentViewportHeightPx}
            fallbackScrollIndex={4}
          />
        </>
      )}
      {controller.precision === 'second' && (
        <>
          <span
            style={
              contentViewportHeightPx
                ? { height: contentViewportHeightPx }
                : undefined
            }
            className={cn(
              'tw:flex tw:items-center tw:text-lg tw:font-medium',
              !contentViewportHeightPx &&
                (fullHeight
                  ? 'tw:h-full'
                  : (columnViewportHeightClassName ?? 'tw:h-40'))
            )}
          >
            :
          </span>
          <TimeColumn
            label="Second"
            options={controller.seconds}
            selected={controller.timeParts.second}
            formatOption={formatPaddedOption}
            onSelect={(option) =>
              controller.commit({
                ...controller.timeParts,
                second: Number(option),
              })
            }
            onDeselect={() => controller.clearSegment('second')}
            scrollTrigger={controller.scrollTrigger}
            fullHeight={fullHeight}
            viewportHeightClassName={columnViewportHeightClassName}
            viewportHeightPx={contentViewportHeightPx}
            fallbackScrollIndex={4}
          />
        </>
      )}
      {controller.use12Hour && (
        <TimeColumn
          label="Period"
          options={['AM', 'PM']}
          selected={
            controller.timeParts.hour24 !== undefined
              ? getPeriod(controller.timeParts.hour24)
              : controller.timeParts.period
          }
          onSelect={(option) => controller.updatePeriod(option as 'AM' | 'PM')}
          onDeselect={() => controller.clearSegment('period')}
          scrollTrigger={controller.scrollTrigger}
          fullHeight={fullHeight}
          viewportHeightClassName={fullHeight ? undefined : 'tw:h-40'}
          viewportHeightPx={contentViewportHeightPx}
        />
      )}
    </div>
  );
}

type TimePickerProps = TimePickerRenderProps &
  (
    | { controller: TimePickerController }
    | (UseTimePickerOptions & { controller?: never })
  );

function TimePickerFromOptions({
  className,
  fullHeight = false,
  columnViewportHeightClassName,
  viewportHeightPx,
  ...options
}: TimePickerRenderProps & UseTimePickerOptions) {
  const controller = useTimePicker(options);

  return (
    <TimePickerFromController
      controller={controller}
      className={className}
      fullHeight={fullHeight}
      columnViewportHeightClassName={columnViewportHeightClassName}
      viewportHeightPx={viewportHeightPx}
    />
  );
}

// Dispatches to whichever implementation matches how it was called: pass plain config
// (UseTimePickerOptions) for a self-contained picker, or pass an externally-built
// `controller` (e.g. from TimePickerField, which shares one controller between its
// segmented text inputs and this popover content) to render from that instead. Calls no
// hooks itself so each branch's own hook calls stay unconditional (Rules of Hooks).
function TimePicker({
  className,
  fullHeight = false,
  columnViewportHeightClassName,
  viewportHeightPx,
  ...rest
}: TimePickerProps) {
  if (rest.controller) {
    return (
      <TimePickerFromController
        controller={rest.controller}
        className={className}
        fullHeight={fullHeight}
        columnViewportHeightClassName={columnViewportHeightClassName}
        viewportHeightPx={viewportHeightPx}
      />
    );
  }

  return (
    <TimePickerFromOptions
      {...rest}
      className={className}
      fullHeight={fullHeight}
      columnViewportHeightClassName={columnViewportHeightClassName}
      viewportHeightPx={viewportHeightPx}
    />
  );
}

export { TimePicker, useTimePicker };
export type {
  TimePickerProps,
  TimePickerController,
  TimePopoverLayout,
  TimePrecision,
  UseTimePickerOptions,
};
